import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import * as turf from '@turf/turf';
import * as React from 'react';
import { useRef, useState } from 'react';
import {
  BooleanInput,
  ChipField,
  Datagrid,
  EditButton,
  FunctionField,
  Link,
  List,
  Loading,
  ReferenceInput,
  SearchInput,
  SimpleForm,
  TextField,
  useCreatePath,
  useGetOne,
  useNotify,
  useRecordContext,
  useTranslate,
  AutocompleteInput,
} from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import TextFieldNullable from '../../../components/fields/TextFieldNullable';
import authProvider from '../../../utils/authProvider';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import CustomBulkActionButtons from '../../../components/buttons/CustomBulkActionButtons';
import { StabilityAlert, StabilityLevel } from '../../../components/StabilityAlert';
import { atsDataProvider } from '../../../dataProviders/atsDataProvider';
import { xgacDataProvider } from '../../../dataProviders/xgacDataProvider';
import { DateFieldWithTime } from '../../../components/fields/DateFieldWithTime';
import { CloneProcessTemplateButton } from '../components/ats/CloneProcessTemplateButton';
import { ProcessTemplateForm } from '../components/ats/ProcessTemplateForm';
import { ProcessTemplateFormToolbar } from '../components/ats/ProcessTemplateFormToolbar';
import {
  transformProcessTemplateForClone,
  transformProcessTemplateForSave,
} from '../components/ats/processTemplateTransforms';
import { validateProcessTemplateForm } from '../components/ats/processTemplateValidation';
import { createEmptyScheduleTask } from '../components/ats/scheduleTaskDefaults';

export const SimulateProcessButton = () => {

  const translate = useTranslate();
  const [preDialogOpen, setPreDialogOpen] = useState(false);
  const [mainDialogOpen, setMainDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [atsSimulateResponse, setAtsSimulateResponse] = useState<React.ReactNode[] | null>(null);

  const notify = useNotify();
  const record = useRecordContext();
  const createPath = useCreatePath();

  if (!record) return null;

  const getTaskTemplateLabel = (task: {
    taskTemplate?: string | { id?: string; templateName?: string };
  } | undefined) => {

    const taskTemplate = task?.taskTemplate;
    if (!taskTemplate) return translate('resources.process-templates.text.step_not_configured', { _: 'Not configured' });
    if (typeof taskTemplate === 'string') return taskTemplate;
    return taskTemplate.templateName || taskTemplate.id || translate('resources.process-templates.text.step_not_configured', { _: 'Not configured' });

  };

  const simulateProcess = async () => {

    if (!selectedAsset) {

      setPreDialogOpen(false);
      notify('resources.process-templates.text.no_asset_error', { type: 'error' });
      return;

    }

    setPreDialogOpen(false);
    setMainDialogOpen(true);
    setAtsSimulateResponse(null);

    const atsRequest = await atsDataProvider.simulate(record.id.toString(), selectedAsset);
    if (!atsRequest) {

      setMainDialogOpen(false);
      notify('resources.process-templates.text.simulation_error', { type: 'error' });
      return;

    }

    const atsSimulateCardList: React.ReactNode[] = [];

    let countDelay = true;
    for (const key of Object.keys(atsRequest.json)) {

      const index = Object.keys(atsRequest.json).indexOf(key);

      const currentTask = record.scheduleTasks[index];

      if (typeof atsRequest.json[key][0]?.alarmCenter !== 'undefined') {

        const alarmCenters = atsRequest.json[key];

        const alarmCenterTableRows = [];

        for (const currentCenter of alarmCenters) {

          alarmCenterTableRows.push(
            <TableRow key={currentCenter.alarmCenter?.id}>
              <TableCell>
                <span style={{ color: 'grey' }}>{currentCenter.querySpeed} ms</span>
              </TableCell>
              <TableCell>
                <Link to={createPath({ resource: 'customers', type: 'edit', id: currentCenter.alarmCenter.id })}
                  target="_blank">{currentCenter.alarmCenter.name}
                  <OpenInNewIcon sx={{ fontSize: '0.875rem' }}/>
                </Link>
              </TableCell>
              <TableCell>
                {currentCenter.alarmCenter.escalate ? translate('general.text.yes') : translate('general.text.no')}
              </TableCell>
              <TableCell>
                {
                  currentCenter.alarmCenter.queue ? <span>{currentCenter.alarmCenter.queue}</span> : <span className="gray">
                    {translate('general.text.none').charAt(0).toUpperCase() + translate('general.text.none').slice(1)}
                  </span>
                }
              </TableCell>
            </TableRow>,
          );

        }

        atsSimulateCardList.push(
          <Card sx={{ marginBottom: '5px' }} variant="outlined" key={key}>
            <CardContent>
              <Typography variant="h6" >{index + 1}: {getTaskTemplateLabel(currentTask)}</Typography>
              <p style={{ fontSize: '12px', color: 'gray', marginTop: '3px' }} className={countDelay ? '' : 'line-through'}>
                {translate('resources.process-templates.text.previous_stage_delay', { delay: currentTask.delay / 1000 })}
              </p>
              {alarmCenters.length > 0 && (
                <Table sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'grey' }}>
                        {translate('resources.process-templates.text.query_speed')}
                      </TableCell>
                      <TableCell>
                        {translate('general.fields.name')}
                      </TableCell>
                      <TableCell>
                        {translate('resources.process-templates.text.alarmcenter_escalate')}
                      </TableCell>
                      <TableCell>
                        {translate('resources.process-templates.text.alarmcenter_queue_name')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alarmCenterTableRows}
                  </TableBody>
                </Table>
              )}
              {alarmCenters.length === 0 && (
                <span>{translate('resources.process-templates.text.no_results_in_stage')}</span>
              )}
            </CardContent>
          </Card>,
        );

        countDelay = alarmCenters.length !== 0;

      } else {

        const creatorAsset = await xgacDataProvider.getOne('assets', { id: selectedAsset });

        const creatorPosition = turf.point(creatorAsset.data?.position?.coordinates || [0, 0]);

        const assets = atsRequest.json[key];

        const assetRows = [];

        for (const currentAsset of assets) {

          let validDistance = true;
          const fromPosition = currentAsset.position?.coordinates;

          if (!fromPosition || !creatorPosition || (creatorPosition.geometry.coordinates[0] === 0 && creatorPosition.geometry.coordinates[1] === 0)) {

            validDistance = false;

          }
          const distance = turf.distance(creatorPosition, turf.point(fromPosition || [0, 0])).toFixed(2);

          assetRows.push(
            <TableRow key={currentAsset._id}>
              <TableCell>
                <span style={{ color: 'grey' }}>{currentAsset.querySpeed} ms</span>
              </TableCell>
              <TableCell>
                <Link to={createPath({ resource: 'app-users', type: 'edit', id: currentAsset._id })}
                  target="_blank">{currentAsset.name}
                  <OpenInNewIcon sx={{ fontSize: '0.875rem' }}/>
                </Link>
              </TableCell>
              <TableCell>
                {validDistance && distance
                  ? `${distance} ${translate('resources.process-templates.text.kilometers')}`
                  : translate('general.text.invalid')}
              </TableCell>
              <TableCell>
                {currentAsset.properties?.phoneNumber}
              </TableCell>
              <TableCell>
                {currentAsset.properties?.smsNumber}
              </TableCell>
            </TableRow>,
          );

        }

        atsSimulateCardList.push(
          <Card sx={{ marginBottom: '5px' }} variant="outlined" key={key}>
            <CardContent>
              <Typography variant="h6" >{index + 1}: {getTaskTemplateLabel(currentTask)}</Typography>
              <p style={{ fontSize: '12px', color: 'gray', marginTop: '3px' }} className={countDelay ? '' : 'line-through'}>
                {translate('resources.process-templates.text.previous_stage_delay', { delay: currentTask.delay / 1000 })}
              </p>
              {assets.length > 0 && (
                <Table sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'grey' }}>
                        {translate('resources.process-templates.text.query_speed')}
                      </TableCell>
                      <TableCell>
                        {translate('general.fields.name')}
                      </TableCell>
                      <TableCell>
                        {translate('resources.process-templates.text.asset_distance')}
                      </TableCell>
                      <TableCell>
                        {translate('resources.assets.fields.phoneNumber')}
                      </TableCell>
                      <TableCell>
                        {translate('resources.assets.fields.smsNumber')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assetRows}
                  </TableBody>
                </Table>
              )}
              {assets.length === 0 && (
                <span>{translate('resources.process-templates.text.no_results_in_stage')}</span>
              )}
            </CardContent>
          </Card>,
        );

        countDelay = assets.length !== 0;

      }

    }

    setAtsSimulateResponse(atsSimulateCardList);

  };

  return (
    <>
      <Card sx={{ mb: 2, width: '100%' }}>
        <CardContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 2,
            '&:last-child': { pb: 2 },
          }}
        >
          <Button onClick={() => setPreDialogOpen(true)}>
            {translate('resources.process-templates.text.simulate_process')}
          </Button>
          <Box sx={{ ml: 'auto' }}>
            <CloneProcessTemplateButton />
          </Box>
        </CardContent>
      </Card>
      <Dialog open={preDialogOpen} onClose={() => setPreDialogOpen(false)}>
        <DialogTitle className="flex-in-between">
          <span> {`${translate('resources.process-templates.text.simulate_process')} ${record.processName}`} </span>
          <IconButton onClick={() => setPreDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <ReferenceInput source="asset" reference="all-assets" record={{ id: 0, asset: null }}>
            <AutocompleteInput onChange={(event) => setSelectedAsset(event)} label={'resources.assets.text.single_name'} />
          </ReferenceInput>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreDialogOpen(false)} autoFocus>
            <>{translate('ra.action.cancel')}</>
          </Button>
          <Button onClick={simulateProcess} disabled={selectedAsset === null}>{translate('resources.process-templates.text.simulate')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={mainDialogOpen} onClose={() => setMainDialogOpen(false)} fullWidth={true} maxWidth="xl">
        <DialogTitle className="flex-in-between">
          <span> {`${translate('resources.process-templates.text.simulate_process')} ${record.processName}`} </span>
          <IconButton onClick={() => setMainDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!atsSimulateResponse && (
            <Loading
              loadingPrimary="resources.process-templates.text.simulation_started"
              loadingSecondary="resources.process-templates.text.simulation_started_secondary"
            />
          )}
          {atsSimulateResponse && atsSimulateResponse}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMainDialogOpen(false)} autoFocus>
            <>{translate('general.text.close')}</>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

};

const isDeveloperAdmin = () => authProvider.getRoles().includes('developer_admin');

const GlobalTemplateField = () => {

  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || record.xgacCustomerId != null) return null;
  return <ChipField record={{ label: translate('resources.process-templates.text.global_badge') }} source="label" size="small" />;

};

const ProcessTemplateEditForm = () => {

  const record = useRecordContext();
  const translate = useTranslate();
  const isGlobal = !!record && record.xgacCustomerId == null;
  const readOnly = isGlobal && !isDeveloperAdmin();

  return (
    <>
      {isGlobal && (
        <Alert severity={readOnly ? 'info' : 'warning'} sx={{ mb: 1 }}>
          {translate(readOnly
            ? 'resources.process-templates.text.global_edit_warning'
            : 'resources.process-templates.text.global_edit_developer_warning')}
        </Alert>
      )}
      <SimpleForm
        key={record?.id}
        validate={validateProcessTemplateForm}
        toolbar={readOnly ? false : <ProcessTemplateFormToolbar />}
      >
        <SimulateProcessButton/>
        <ProcessTemplateForm disableScope disabled={readOnly} />
      </SimpleForm>
    </>
  );

};

export const ProcessTemplateList = () => {

  const developerAdmin = isDeveloperAdmin();
  const templateFilters = [
    <SearchInput source="search" alwaysOn sx={{ width: '100%' }} variant="filled"/>,
    <BooleanInput
      source="includeSystemOwned"
      label="resources.process-templates.text.include_global_templates"
      alwaysOn
      sx={{
        flexShrink: 0,
        pb: '10px',
        '& .MuiFormControlLabel-label': {
          whiteSpace: 'nowrap',
        },
      }}
    />,
  ];

  return (
    <>
      <StabilityAlert stability={StabilityLevel.Unstable} />
      <List
        title="resources.process-templates.text.title"
        filters={templateFilters}
        filterDefaultValues={{ includeSystemOwned: false }}
      >
        <Datagrid
          rowClick="toggleSelection"
          bulkActionButtons={<CustomBulkActionButtons/>}
          isRowSelectable={(record) => record.xgacCustomerId != null || developerAdmin}
        >
          <TextField source="processName" />
          <TextFieldNullable source="description" />
          <GlobalTemplateField />
          <DateFieldWithTime source="createdAt" label="general.fields.createdAt" timeOnHover={true}/>
          <DateFieldWithTime source="updatedAt" label="general.fields.updatedAt" timeOnHover={true}/>
          <FunctionField render={(record) => <CloneProcessTemplateButton record={record} />} />
          <EditButton/>
        </Datagrid>
      </List>
    </>
  );

};

export const ProcessTemplateCreate = () => {

  const [searchParams] = useSearchParams();
  const cloneFrom = searchParams.get('cloneFrom');
  const { data, error } = useGetOne(
    'process-templates',
    { id: cloneFrom || '' },
    { enabled: !!cloneFrom },
  );
  const defaultValuesRef = useRef<{ cloneFrom: string | null; values: Record<string, unknown> } | null>(null);

  // Wait for the clone query to settle. Freezing defaults while it can still deliver a
  // record would lock in blank ones that a later successful fetch can no longer replace.
  if (cloneFrom && !data && !error) {

    return <Loading />;

  }

  if (defaultValuesRef.current !== null && defaultValuesRef.current.cloneFrom !== cloneFrom) {

    defaultValuesRef.current = null;

  }

  // ra-core Form feeds defaultValues into RHF `values`. A new object (new step UUID)
  // on re-render resets the whole form and wipes in-progress edits.
  if (defaultValuesRef.current === null) {

    const clonedValues = cloneFrom && data
      ? transformProcessTemplateForClone(data as Record<string, unknown>)
      : {
        scope: 'customer',
        scheduleTasks: [createEmptyScheduleTask()],
        processName: '',
        description: '',
      };
    // Cloning a global template must not produce a new global one without the developer_admin role.
    const values = isDeveloperAdmin() ? clonedValues : { ...clonedValues, scope: 'customer' };
    defaultValuesRef.current = { cloneFrom, values };

  }

  const defaultValues = defaultValuesRef.current.values;

  return (
    <>
      <StabilityAlert stability={StabilityLevel.Unstable} />
      <CreateWithSuccess
        title="resources.process-templates.text.title"
        transform={(formData) => transformProcessTemplateForSave(formData as Record<string, unknown>, true)}
      >
        <SimpleForm
          defaultValues={defaultValues}
          validate={validateProcessTemplateForm}
          toolbar={<ProcessTemplateFormToolbar />}
        >
          <ProcessTemplateForm />
        </SimpleForm>
      </CreateWithSuccess>
    </>
  );

};

export const ProcessTemplateEdit = () => (
  <>
    <StabilityAlert stability={StabilityLevel.Unstable} />
    <EditWithSuccess
      title="resources.process-templates.text.title"
      transform={(formData: Record<string, unknown>) => transformProcessTemplateForSave(formData, false)}
      queryOptions={{ refetchOnReconnect: false }}
    >
      <ProcessTemplateEditForm />
    </EditWithSuccess>
  </>
);
